// NContro legal document data — structured for consistent rendering.
// Each article has a number, heading, and body (paragraphs, lists, and optional sub-lists).
// Add doc #2 (Vendor Terms) and doc #3 (?) to this folder as separate files.

export const CUSTOMER_TOS = {
  slug: "customer-tos",
  title: "Customer Terms of Service",
  role_label: "Customer Agreement",
  company: "NContro",
  short_company: "NContro",
  effective_date: "August 21, 2026",
  version: "2.0",
  intro: [
    'Welcome to NContro ("Company," "Platform," "we," "our," or "us").',
    'These Customer Terms of Service ("Terms") govern your access to and use of the Platform, including our website, mobile application, and related services.',
    "By creating an account, browsing the Platform, requesting quotes, booking services, making payments, or otherwise using the Platform, you agree to be legally bound by these Terms. If you do not agree, you may not use the Platform.",
  ],
  articles: [
    {
      n: 1,
      title: "Important Summary",
      body: [
        { p: "Before using the Platform, please understand the following:" },
        {
          list: [
            "We are an online marketplace that connects customers with independent vendors.",
            "We do not provide catering, venue, entertainment, photography, floral, rental, planning, or other event services.",
            "Each vendor is solely responsible for the services they provide.",
            "Bookings are not confirmed until the required deposit has been successfully processed.",
            "Each vendor establishes its own pricing, cancellation policy, refund policy, and payment schedule.",
            "The Platform is not responsible for disputes regarding the quality or performance of vendor services.",
            "By using the Platform, you agree to these Terms.",
          ],
        },
        { p: "This summary is provided for convenience only. In the event of any conflict, the complete Terms control." },
      ],
    },
    {
      n: 2,
      title: "Definitions",
      body: [
        { p: "For purposes of these Terms:" },
        {
          defs: [
            ["Account", "a registered customer account created on the Platform."],
            ["Booking", "a confirmed reservation between a Customer and Vendor after acceptance of a proposal and successful payment of the required deposit."],
            ["Customer", "any individual or entity seeking event-related services through the Platform."],
            ["Deposit", "the initial payment required by a Vendor to reserve an event date."],
            ["Event", "any wedding, corporate function, party, meeting, fundraiser, celebration, festival, or other occasion for which services are requested."],
            ["Platform", "the Company's website, mobile application, software, and related services."],
            ["Proposal", "a Vendor's written quote containing pricing, services, payment schedule, cancellation terms, and other conditions."],
            ["Vendor", "an independent business or individual offering services through the Platform."],
          ],
        },
      ],
    },
    {
      n: 3,
      title: "Eligibility",
      body: [
        { p: "You represent and warrant that:" },
        {
          list: [
            "You are at least eighteen (18) years of age or otherwise have legal capacity to enter into a binding contract.",
            "All information you provide is true, accurate, and complete.",
            "You will maintain accurate account information.",
            "You will comply with all applicable laws while using the Platform.",
          ],
        },
        { p: "The Company reserves the right to suspend or terminate accounts created using false information." },
      ],
    },
    {
      n: 4,
      title: "The Platform",
      body: [
        { p: "The Platform is an online marketplace designed to connect customers with independent vendors offering event-related services." },
        { p: "The Company does not employ, supervise, direct, control, or manage vendors." },
        { p: "Nothing in these Terms creates an employer-employee relationship, partnership, joint venture, agency, or franchise relationship between the Company and any Vendor." },
        { p: "The Company is not a party to the service agreement entered into between Customers and Vendors." },
      ],
    },
    {
      n: 5,
      title: "Account Registration",
      body: [
        { p: "To access certain features, you must create an account." },
        { p: "You agree to:" },
        {
          list: [
            "maintain the confidentiality of your login credentials;",
            "immediately notify us of unauthorized account activity;",
            "provide accurate information; and",
            "be responsible for all activity occurring under your account.",
          ],
        },
        { p: "We reserve the right to suspend or terminate accounts that violate these Terms." },
      ],
    },
    {
      n: 6,
      title: "Booking Process",
      body: [
        { h: "Step One — Request for Services" },
        { p: "Customers may submit requests for services through the Platform by providing event details including, but not limited to:" },
        {
          list: ["Event date;", "Event location;", "Estimated guest count;", "Requested services;", "Budget;", "Special instructions."],
        },
        { p: "Submitting a request does not create a contract." },

        { h: "Step Two — Vendor Proposal" },
        { p: "A Vendor may respond by submitting a Proposal, which may include:" },
        {
          list: ["Scope of services;", "Pricing;", "Deposit amount;", "Estimated total cost;", "Payment schedule;", "Cancellation policy;", "Refund policy;", "Additional fees;", "Other applicable terms."],
        },
        { p: "Each Proposal constitutes an offer by the Vendor." },

        { h: "Step Three — Acceptance" },
        { p: "A Booking is created only when the Customer accepts the Proposal through the Platform." },
        { p: "By accepting a Proposal, the Customer agrees to the Vendor's pricing, payment schedule, cancellation policy, refund policy, and any additional disclosed terms." },

        { h: "Step Four — Deposit" },
        { p: "Following acceptance, the Customer shall pay the required Deposit." },
        { p: "No Booking is confirmed until payment has been successfully processed." },

        { h: "Step Five — Confirmation" },
        { p: "Once payment is successfully processed, the Platform will issue a booking confirmation." },
        { p: "Confirmation reserves the requested services subject to these Terms and the Vendor's policies." },

        { h: "Step Six — Remaining Balance" },
        { p: "Any remaining balance shall be paid according to the Vendor's payment schedule." },
        { p: "Failure to make timely payments may result in cancellation of the Booking." },
      ],
    },
    {
      n: 7,
      title: "Payments",
      body: [
        { p: "Payments may include:" },
        {
          list: ["Deposits;", "Remaining balances;", "Applicable taxes;", "Processing fees;", "Platform service fees, if applicable."],
        },
        { p: "Payments are processed through third-party payment processors." },
        { p: "The Company does not guarantee uninterrupted payment processing and shall not be liable for delays caused by financial institutions or payment processors." },
      ],
    },
    {
      n: 8,
      title: "Cancellations",
      body: [
        { p: "Each Vendor establishes its own cancellation and refund policies." },
        { p: "Customers are responsible for reviewing those policies before making payment." },
        { p: "Unless required by applicable law:" },
        {
          list: ["Deposits may be non-refundable.", "Processing fees may be non-refundable.", "Platform fees may be non-refundable."],
        },
        { p: "Customers acknowledge that cancellation policies differ between Vendors." },
      ],
    },
    {
      n: 9,
      title: "Customer Responsibilities",
      body: [
        { p: "Customers agree to:" },
        {
          list: ["provide accurate event information;", "communicate respectfully;", "make timely payments;", "comply with venue rules;", "refrain from unlawful conduct;", "cooperate with Vendors."],
        },
        { p: "Customers shall not:" },
        {
          list: ["threaten Vendors;", "submit fraudulent bookings;", "abuse the payment system;", "create fake accounts;", "submit false reviews;", "interfere with Platform operations."],
        },
      ],
    },
    {
      n: 10,
      title: "Reviews",
      body: [
        { p: "Customers may submit reviews following completed Bookings." },
        { p: "Reviews must:" },
        {
          list: ["reflect honest experiences;", "not contain false statements;", "not contain defamatory content;", "not include unlawful, abusive, or discriminatory language."],
        },
        { p: "The Company reserves the right to remove reviews that violate these Terms." },
      ],
    },
    {
      n: 11,
      title: "Platform Disclaimer",
      body: [
        { p: "The Company does not guarantee:" },
        {
          list: ["Vendor availability;", "Vendor performance;", "Quality of services;", "Food quality;", "Timeliness;", "Customer satisfaction;", "Event outcomes."],
        },
        { p: "The Platform merely facilitates introductions and transactions between Customers and Vendors." },
      ],
    },
    {
      n: 12,
      title: "Limitation of Liability",
      body: [
        { p: "To the fullest extent permitted by applicable law, the Company's total liability arising out of or relating to these Terms or the use of the Platform shall not exceed the total Platform service fees actually paid by the Customer to the Company for the Booking giving rise to the claim." },
        { p: "The Company shall not be liable for indirect, incidental, consequential, special, punitive, or exemplary damages, including lost profits, lost business opportunities, emotional distress, or damages arising from Vendor performance or non-performance." },
        { p: "Nothing in these Terms limits liability where such limitation is prohibited by applicable law." },
      ],
    },
    {
      n: 13,
      title: "Indemnification",
      body: [
        { p: "You agree to defend, indemnify, and hold harmless the Company and its officers, directors, employees, affiliates, and agents from and against any claims, damages, liabilities, losses, costs, and expenses, including reasonable attorneys' fees, arising out of or related to your use of the Platform, your violation of these Terms, or your interactions with any Vendor or other user." },
      ],
    },
    {
      n: 14,
      title: "Termination",
      body: [
        { p: "The Company may suspend or terminate your account at any time for violations of these Terms, fraudulent activity, abuse of the Platform, unlawful conduct, or any activity that poses a risk to the Company, its users, or the integrity of the Platform." },
        { p: "Termination does not affect any payment obligations or rights that accrued prior to termination." },
      ],
    },
    {
      n: 15,
      title: "Governing Law",
      body: [
        { p: "These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Virginia, without regard to its conflict of law principles." },
        { p: "Venue and jurisdiction for any legal action shall be as provided in the Company's Dispute Resolution provisions or, where applicable, in the courts located in the Commonwealth of Virginia." },
      ],
    },
    {
      n: 16,
      title: "Dispute Resolution & Arbitration",
      body: [
        { h: "16.1 Informal Resolution" },
        { p: "Before initiating any legal proceeding, arbitration, or other formal dispute resolution process, you agree to first contact the Company in writing and make a good-faith effort to resolve the dispute informally." },
        { p: "The written notice must include:" },
        {
          list: [
            "Your full name and contact information;",
            "A description of the dispute;",
            "Any supporting documentation; and",
            "The specific relief requested.",
          ],
        },
        { p: "The parties agree to attempt to resolve the dispute informally for at least thirty (30) days before commencing arbitration or litigation." },

        { h: "16.2 Agreement to Arbitrate" },
        { p: "Except where prohibited by applicable law, any dispute, claim, or controversy arising out of or relating to these Terms, the Platform, your Account, any Booking, or your use of the Platform shall be resolved exclusively through binding arbitration rather than in court." },
        { p: "Arbitration shall be conducted by a neutral arbitrator in accordance with the applicable consumer arbitration rules of the arbitration provider selected by the Company, unless the parties mutually agree otherwise." },
        { p: "The arbitrator shall have exclusive authority to resolve any dispute concerning the interpretation, applicability, enforceability, or formation of this arbitration agreement." },

        { h: "16.3 Individual Claims Only" },
        { p: "To the fullest extent permitted by law, all claims shall be brought solely in an individual capacity." },
        { p: "Neither you nor the Company shall participate as a plaintiff, claimant, class representative, class member, or private attorney general in any class action, consolidated action, representative action, or collective proceeding." },
        { p: "The arbitrator shall have no authority to consolidate claims involving multiple parties unless all parties expressly consent in writing." },

        { h: "16.4 Exceptions" },
        { p: "Nothing in this Article shall prevent either party from:" },
        {
          list: [
            "filing an individual claim in a court having jurisdiction over small claims matters, provided the claim qualifies;",
            "seeking temporary or preliminary injunctive relief when necessary to prevent immediate and irreparable harm;",
            "pursuing claims that applicable law requires to be heard in a court of competent jurisdiction.",
          ],
        },

        { h: "16.5 Governing Law" },
        { p: "This arbitration agreement shall be governed by the Federal Arbitration Act (\"FAA\") to the fullest extent permitted by law. To the extent state law applies, the laws of the Commonwealth of Virginia shall govern without regard to its conflict of laws principles." },

        { h: "16.6 Venue" },
        { p: "If a dispute is determined by a court to be non-arbitrable, or if arbitration is otherwise unavailable, the parties agree that exclusive jurisdiction and venue shall lie in the state or federal courts located in the Commonwealth of Virginia, and each party consents to the personal jurisdiction of those courts." },

        { h: "16.7 Time Limitation" },
        { p: "To the fullest extent permitted by applicable law, any claim arising out of or relating to these Terms or the use of the Platform must be commenced within one (1) year after the claim arises. Claims not brought within that period shall be permanently barred, except where a longer period is required by applicable law." },

        { h: "16.8 Recovery of Costs" },
        { p: "Unless otherwise required by applicable law or the applicable arbitration rules, each party shall bear its own attorneys' fees, costs, and expenses. The arbitrator may award costs or attorneys' fees only where authorized by law or these Terms." },

        { h: "16.9 Severability" },
        { p: "If any portion of this Article is determined to be invalid or unenforceable, the remaining provisions shall remain in full force and effect to the maximum extent permitted by law. If the prohibition on class or representative actions is found unenforceable, then this entire arbitration provision shall be null and void only with respect to that claim, and the remaining provisions of these Terms shall continue in effect." },
      ],
    },
    {
      n: 17,
      title: "Release, Disclaimers, and Limitation of Liability",
      body: [
        { h: "17.1 Marketplace Only" },
        { p: "The Platform is an online marketplace that enables Customers to locate, communicate with, and book independent Vendors offering event-related products and services." },
        { p: "The Company does not own, employ, supervise, direct, manage, control, endorse, or guarantee any Vendor or the services they provide." },
        { p: "Each Vendor is solely responsible for:" },
        {
          list: [
            "the products and services they offer;",
            "pricing;",
            "scheduling;",
            "food quality;",
            "service quality;",
            "staffing;",
            "delivery;",
            "setup and cleanup;",
            "compliance with applicable laws;",
            "licenses and permits;",
            "insurance;",
            "health and safety practices; and",
            "fulfillment of all contractual obligations.",
          ],
        },
        { p: "The Company is not a party to any contract between a Customer and a Vendor other than facilitating payments made through the Platform." },

        { h: "17.2 No Warranty" },
        { p: "The Platform and all related services are provided on an \"AS IS\" and \"AS AVAILABLE\" basis." },
        { p: "To the fullest extent permitted by law, the Company expressly disclaims all warranties, whether express, implied, statutory, or otherwise, including, without limitation:" },
        {
          list: [
            "merchantability;",
            "fitness for a particular purpose;",
            "title;",
            "non-infringement;",
            "uninterrupted availability;",
            "security;",
            "accuracy; and",
            "reliability.",
          ],
        },
        { p: "The Company does not warrant that:" },
        {
          list: [
            "Vendors will perform services as described;",
            "Vendors will appear at scheduled events;",
            "food or beverages will meet expectations;",
            "event services will be uninterrupted;",
            "communications between users will be error-free;",
            "payments will always process successfully; or",
            "the Platform will be available at all times.",
          ],
        },

        { h: "17.3 Vendor Performance" },
        { p: "The Company does not guarantee the quality, legality, safety, timeliness, suitability, professionalism, or performance of any Vendor." },
        { p: "Customers acknowledge that all decisions to hire a Vendor are made at their own discretion and risk." },
        { p: "The Company encourages Customers to review Vendor profiles, ratings, reviews, credentials, and communications before entering into a Booking." },

        { h: "17.4 Customer Responsibility" },
        { p: "Customers are solely responsible for evaluating whether a Vendor is appropriate for their event." },
        { p: "The Company does not independently investigate, inspect, certify, or guarantee the qualifications, experience, licensing, insurance, food safety practices, financial condition, or legal compliance of Vendors unless expressly stated on the Platform." },

        { h: "17.5 Release of Claims" },
        { p: "To the fullest extent permitted by applicable law, Customers voluntarily release and discharge the Company, its owners, officers, directors, employees, affiliates, successors, assigns, contractors, and agents from any and all claims, demands, damages, liabilities, losses, costs, and expenses arising out of or relating to:" },
        {
          list: [
            "Vendor performance or non-performance;",
            "cancellation of services;",
            "delays;",
            "missed appointments;",
            "food quality;",
            "food safety;",
            "allergic reactions;",
            "alcohol service;",
            "property damage;",
            "personal injury;",
            "death;",
            "negligence by a Vendor;",
            "employee actions of a Vendor;",
            "subcontractors retained by a Vendor;",
            "event postponements;",
            "weather;",
            "acts of God;",
            "force majeure events;",
            "disputes regarding pricing;",
            "disputes regarding refunds;",
            "disputes regarding deposits;",
            "dissatisfaction with services;",
            "inaccurate Vendor listings; or",
            "any transaction occurring between a Customer and Vendor.",
          ],
        },
        { p: "Nothing in this section releases the Company from liability that cannot legally be waived under applicable law." },

        { h: "17.6 Payment Processing" },
        { p: "Payments made through the Platform are processed by independent third-party payment processors." },
        { p: "The Company shall not be liable for:" },
        {
          list: [
            "payment delays;",
            "declined transactions;",
            "banking errors;",
            "processing interruptions;",
            "unauthorized use of payment methods;",
            "chargebacks initiated by financial institutions; or",
            "payment processor outages.",
          ],
        },

        { h: "17.7 Limitation of Liability" },
        { p: "To the fullest extent permitted by applicable law, the Company's total cumulative liability arising out of or relating to the Platform, these Terms, or any Booking shall not exceed the total Platform service fees actually retained by the Company for the Booking giving rise to the claim." },
        { p: "Under no circumstances shall the Company be liable for any indirect, incidental, consequential, exemplary, punitive, special, or enhanced damages, including, without limitation:" },
        {
          list: [
            "lost profits;",
            "lost income;",
            "lost business opportunities;",
            "emotional distress;",
            "reputational harm;",
            "event cancellation costs;",
            "substitute vendor costs;",
            "travel expenses;",
            "hotel expenses;",
            "wedding expenses;",
            "corporate event expenses;",
            "lost deposits paid to Vendors; or",
            "any other consequential damages,",
          ],
        },
        { p: "even if the Company has been advised of the possibility of such damages." },

        { h: "17.8 No Guarantee of Event Success" },
        { p: "The Company makes no representation or guarantee regarding the success of any event, the availability of any Vendor, or the outcome of any services obtained through the Platform." },
        { p: "Customers acknowledge that event planning involves risks beyond the Company's control and assume those risks when using the Platform." },

        { h: "17.9 Maximum Protection Permitted by Law" },
        { p: "The limitations, exclusions, releases, and disclaimers contained in this Article shall apply to the fullest extent permitted under applicable law." },
        { p: "If any portion of this Article is determined to be unenforceable, the remaining provisions shall remain in full force and effect." },
      ],
    },
    {
      n: 18,
      title: "Booking Agreement; Accepted Terms Govern",
      body: [
        { p: "When a Customer accepts a Vendor Proposal on the Platform, the accepted Proposal together with the booking-specific terms displayed and accepted at that time forms the agreement between the Customer and Vendor for that Booking (the \"Booking Agreement\")." },
        { p: "The Booking Agreement includes the applicable elements presented at acceptance, which may include:" },
        {
          list: [
            "Vendor and Customer identity;",
            "Event date, time, and where applicable AM/PM period;",
            "Event location if collected by the Platform;",
            "Scope of services, packages, menus, quantities, or other selected items presented in the Proposal;",
            "Pricing, deposit, remaining balance, and payment schedule;",
            "Cancellation policy and refund policy presented at acceptance;",
            "Fees, taxes, and additional charges included in the Proposal; and",
            "Any other terms expressly included in the accepted Proposal.",
          ],
        },
        { p: "The version accepted for a particular Booking governs that Booking. Later changes to the Vendor's profile, pricing, standard policies, cancellation policy, refund policy, deposit requirements, or other general terms do not retroactively alter an existing Booking Agreement." },
        { p: "The Platform is not a party to the service contract between Customer and Vendor. The Company facilitates the marketplace, Booking process, communications, payments, records, and related Platform functions." },
      ],
    },
    {
      n: 19,
      title: "Booking Modifications",
      body: [
        { p: "Material changes to an accepted Booking — including material changes to scope, price, services, event date, quantities, fees, or other material terms — require the agreement of both Customer and Vendor." },
        { p: "Where the Platform records such a modification, the modification becomes part of the Booking record." },
        { p: "A recorded modification does not erase the historical accepted Booking terms. The prior accepted version remains on file for the parties and the Company." },
      ],
    },
    {
      n: 20,
      title: "Verified Vendor Designation",
      body: [
        { p: "A \"Verified\" designation or badge indicates only that the Company reviewed certain information or documentation submitted by the Vendor under the Company's then-current verification procedures." },
        { p: "Verification is not:" },
        {
          list: [
            "an endorsement of the Vendor;",
            "a guarantee of quality, performance, safety, or event outcome;",
            "a warranty of any kind;",
            "a guarantee of the Vendor's financial condition;",
            "a guarantee that any credential will remain valid indefinitely; or",
            "a determination that the Vendor is suitable for any particular Customer or Event.",
          ],
        },
        { p: "Customers remain solely responsible for evaluating whether a Vendor is appropriate for their Event. Vendors remain solely responsible for maintaining all legally required licenses, permits, insurance, certifications, and other credentials." },
      ],
    },
    {
      n: 21,
      title: "Anti-Circumvention",
      body: [
        { p: "The Platform generates value for Customers and Vendors in part by facilitating the initial connection, Proposal, Booking, and payment workflow." },
        { p: "You agree that you will not intentionally take a Booking generated through the Platform off-platform for the purpose of avoiding Platform fees, commissions, or other charges that would otherwise apply to the Booking." },
        { p: "For clarity, this Article does not prohibit:" },
        {
          list: [
            "ordinary Customer/Vendor communications reasonably necessary to plan or perform an Event;",
            "communications initiated on the Platform that reasonably continue by phone, email, or in person;",
            "lawful relationships that existed independently of the Platform before the Customer connected with the Vendor through the Platform.",
          ],
        },
        { p: "The Company may investigate suspected circumvention and, where reasonably warranted, apply the remedies described elsewhere in these Terms and in the Company's Vendor-facing agreement." },
      ],
    },
    {
      n: 22,
      title: "Disputes, Chargebacks, and Platform Records",
      body: [
        { p: "The Company may preserve, review, compile, and where reasonably necessary provide relevant Platform records when responding to refunds, payment disputes, chargebacks, cancellations, Vendor no-shows, Customer no-shows, fraud investigations, safety complaints, or compliance investigations." },
        { p: "Relevant records may include the accepted Proposal or Booking record, the applicable cancellation and refund policy, timestamps, payment and refund records, Platform communications, cancellation and no-show records, and uploaded supporting documentation." },
        { p: "The Company may provide relevant information to payment processors, financial institutions, regulators, law enforcement, or other appropriate entities where legally permitted and reasonably necessary. Any such disclosure remains subject to the Company's Privacy Policy and applicable law." },
        { p: "The Company is not a court, arbitrator, insurer, guarantor, or ultimate fact-finder in Customer/Vendor service disputes. Nothing in this Article shall be construed to make the Company a party to the underlying service contract between Customer and Vendor." },
      ],
    },
    {
      n: 23,
      title: "Electronic Acceptance",
      body: [
        { p: "Where the Platform properly presents applicable terms to you, electronic actions such as creating an account, checking an acceptance checkbox, clicking an acceptance or confirmation control, accepting a Proposal, or submitting a Booking or payment may constitute your electronic acceptance of those terms." },
        { p: "Merely browsing a public webpage does not by itself constitute acceptance of unrelated transaction-specific terms." },
        { p: "Where the Platform records evidence of acceptance — including timestamps, user identifiers, and applicable document versions — that record may be used as evidence of the acceptance." },
      ],
    },
    {
      n: 24,
      title: "Third-Party Services and Integrations",
      body: [
        { p: "The Platform relies on third-party services (including payment processors, email/notification providers, hosting providers, and calendar or business-tool integrations you may elect to connect)." },
        { p: "The Company does not guarantee uninterrupted operation of independent third-party systems and, to the extent permitted by applicable law, is not responsible for outages, errors, or actions of such third parties. This Article does not, however, broadly disclaim the Company's own legal obligations." },
      ],
    },
    {
      n: 25,
      title: "Incorporated Policies",
      body: [
        { p: "These Terms are supplemented by and incorporate by reference the Company's Privacy Policy and Payment, Cancellation & Refund Policy as in effect from time to time." },
        { p: "In the event of a conflict between these Terms and an incorporated policy on a topic specifically addressed in the incorporated policy, the incorporated policy controls with respect to that topic, except that a Booking-specific term expressly accepted in a Proposal controls for that particular Booking." },
      ],
    },
    {
      n: 26,
      title: "Changes to These Terms",
      body: [
        { p: "The Company may modify these Terms at any time." },
        { p: "Material changes will be communicated through the Platform or by other reasonable means, and where required by applicable law, in advance of taking effect." },
        { p: "Changes to these general Platform Terms do not retroactively modify the Booking-specific Proposal, pricing, cancellation policy, refund policy, or other terms already accepted for an existing Booking, unless both Customer and Vendor agree in writing (including through recorded modification on the Platform) or applicable law requires otherwise." },
        { p: "Continued use of the Platform after changes become effective constitutes acceptance of the revised Terms with respect to future activity." },
      ],
    },
    {
      n: 27,
      title: "Contact Information",
      body: [
        { p: "Questions regarding these Terms may be directed to:" },
        {
          contact: {
            company: "NContro",
            address: "2000 N Beauregard St #345, Alexandria, VA 22311",
            email: "scottye@ncontro.com",
            phone: "(901) 573-7661",
            website: "https://www.ncontro.com",
          },
        },
      ],
    },
  ],
  closing: "By using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Customer Terms of Service.",
};
